/**
 * Phone — Canadian phone-number formatting + normalization helpers.
 *
 * Emitted storage format: `(xxx) xxx-xxxx` (canonical, display-ready).
 * Accepts: any raw input (pasted, legacy formats, bare digits, international
 * with leading 1). Strips everything non-digit, trims to 10, formats.
 *
 * This module mirrors `crewbit-mobile/utils/phone.ts` verbatim. When admin
 * gets a fix, mobile gets the same fix. Cross-repo drift is the only risk
 * and it's guarded by Rule #33 consumer-drift grep on primitive rollouts.
 *
 * Q3 graduates the earlier ad-hoc helpers:
 * - `KioskClientForm.formatPhone` (display-only, never stored canonical)
 * - `importClients/shared/normalize.normalizePhone` (digits-only)
 * - `mobile/utils/phoneNormalize.normalizePhone` (10-digit-or-null)
 *
 * into a single source of truth.
 */

const OPEN_PAREN = '(';
const CLOSE_PAREN = ') ';
const DASH = '-';

/** Strip every non-digit from `raw`. Returns a digits-only string. */
export function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return String(raw).replace(/\D+/g, '');
}

/**
 * Take any raw input and return up to 10 Canadian digits. Leading "1" country
 * code is stripped when the total would otherwise exceed 10. Anything longer
 * is truncated to 10 (users can't type 11+ accidentally; paste is clamped).
 */
export function toCanadian10(raw: string | null | undefined): string {
  const d = digitsOnly(raw);
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return d.slice(0, 10);
}

/**
 * Format a digits string to the canonical `(xxx) xxx-xxxx` shape.
 * Partial input is rendered progressively during typing:
 *   ""    → ""
 *   "5"   → "(5"
 *   "555" → "(555"
 *   "5551"→ "(555) 1"
 *   etc.
 *
 * Input with fewer than 10 digits returns the partial form. 10+ digits
 * returns the full format. The caller is responsible for storing only
 * validated (10-digit) values if desired — this formatter is shape-only.
 */
export function formatPhone(raw: string | null | undefined): string {
  const d = toCanadian10(raw);
  if (d.length === 0) return '';
  if (d.length <= 3) return `${OPEN_PAREN}${d}`;
  if (d.length <= 6) return `${OPEN_PAREN}${d.slice(0, 3)}${CLOSE_PAREN}${d.slice(3)}`;
  return `${OPEN_PAREN}${d.slice(0, 3)}${CLOSE_PAREN}${d.slice(3, 6)}${DASH}${d.slice(6)}`;
}

/**
 * Canonicalize for storage/emission on blur. Empty input stays empty.
 * Any input with at least 10 digits returns the full `(xxx) xxx-xxxx` form.
 * Partial input (< 10 digits) returns whatever the live format is — the
 * input-level validation layer decides whether to accept partial numbers.
 */
export function canonicalPhone(raw: string | null | undefined): string {
  return formatPhone(raw);
}

/**
 * Validate: Canadian phone needs exactly 10 digits (area code + 7).
 * Under-10 returns false; 11-with-leading-1 is normalized to 10 first.
 */
export function isValidPhone(raw: string | null | undefined): boolean {
  return toCanadian10(raw).length === 10;
}

/**
 * Accepts partial input as "possibly valid during typing" — returns true
 * as soon as 7 digits are present (the minimum Canadian exchange + local).
 * Used by form-level valid checks that don't require full area code yet.
 */
export function isLikelyValidPhone(raw: string | null | undefined): boolean {
  return toCanadian10(raw).length >= 7;
}
